import { isInteger } from "@/lib/chillnn/is-integer";

import type { AutoGeneratorForFormModel } from "../../types";

/**
 *
 * @description InputModel へ変換しない、ViewModel のプロパティ名
 *
 * @template {Pick<AddTypeForViewModelType, 'value'>}
 */
const ExcludeKeys: string[] = ["isEdited", "hasError", "error", "isDirty", "isValid"];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const confirmHasValueKeyOfArrayChild = (_array: Array<any>) => _array.some((v) => v["value"] !== undefined);

/**
 *
 * @description 変換を行う再帰関数
 *
 * @param {unknown} _obj - AutoGeneratorForFormModel<T, U>["ViewModel"]
 * @returns {unknown} - AutoGeneratorForFormModel<T, U>["InputModel"]
 */
const converter = (_obj: unknown): unknown => {
	/**
	 *
	 * @description ViewModel の Node まで到達した場合は再帰を終了する
	 *
	 * @template {ForNodeType}
	 */
	if (typeof _obj !== "object" || _obj === null) {
		return _obj;
	}

	if (Array.isArray(_obj)) {
		// 空配列の場合はそのまま返す
		if (_obj.length === 0) return _obj;

		const hasValueKey = confirmHasValueKeyOfArrayChild(_obj);
		// key === 'value' の場合は配列を返して終了
		if (hasValueKey) return _obj.map((v) => v["value"]);

		// key !== 'value' の場合は map 内で再帰
		return _obj.map((v) => converter(v));
	}

	// InputModel
	const obj: { [key: string]: unknown } = {};

	/**
	 *
	 * @example
	 * ```
	 * const object1 = {
	 *   a: 'somestring',
	 *   b: 42,
	 * };
	 * for (const [key, value] of Object.entries(object1)) {
	 *  console.log(`${key}: ${value}`);
	 * }
	 *
	 * Expected output:
	 *   "a: somestring"
	 *   "b: 42"
	 * ```
	 */
	for (const [key, value] of Object.entries(_obj)) {
		// ViewModel のみの概念は除外
		if (ExcludeKeys.includes(key)) continue;

		// key === 'value' と key === `${index}`(配列の時に index が key として処理される) は key のマッピングをしない
		if (key === "value" || isInteger(key)) return converter(value);

		// key のマッピング
		obj[key] = converter(value);
	}

	// ViewModel から変換した InputModel を返す
	return obj;
};

/**
 *
 * @template {I} InputModel - ZodSchemaType
 * @template {E} ViewModel へ変換する際に除外したいプロパティ名
 * @template {Q} QueryData
 */
export const viewModelToInputModelConverter = <I, E, Q>(
	viewModel: AutoGeneratorForFormModel<I, E, Q>["ViewModel"],
): AutoGeneratorForFormModel<I, E, Q>["InputModel"] => {
	const converted = converter(viewModel) as AutoGeneratorForFormModel<I, E, Q>["InputModel"];
	return converted;
};
